/**
 * pullToRefresh -- Utility mobile gesture: tira-abajo para refrescar.
 * PANEL-OPS-V3-001 T3.
 *
 * Solo activa si 'ontouchstart' in window (touch device).
 * Umbral 80px. Throttling 1s entre disparos.
 *
 * Uso:
 *   attachPullToRefresh(containerEl, async () => { await reload(); });
 *
 * @module presentation/shared/pullToRefresh
 */

const THRESHOLD = 80;
const THROTTLE_MS = 1000;

export function attachPullToRefresh(container, onRefresh) {
  if (!container) return () => {};
  if (!('ontouchstart' in window)) return () => {};
  if (container.__ptrBound) return container.__ptrCleanup || (() => {});

  container.classList.add('ptr-container');

  // Inject header (spinner + text)
  let $header = container.querySelector(':scope > .ptr-header');
  if (!$header) {
    $header = document.createElement('div');
    $header.className = 'ptr-header';
    $header.innerHTML = `<span class="ptr-spinner"></span><span class="ptr-text">Desliza para refrescar</span>`;
    container.prepend($header);
  }
  const $text = $header.querySelector('.ptr-text');

  let startY = 0;
  let currentDelta = 0;
  let tracking = false;
  let loading = false;
  let lastFire = 0;

  const setHeight = (h) => { $header.style.height = `${Math.max(0, h)}px`; };
  const setState = (ready, isLoading) => {
    $header.setAttribute('data-ready', ready ? 'true' : 'false');
    $header.setAttribute('data-loading', isLoading ? 'true' : 'false');
    if ($text) {
      if (isLoading) $text.textContent = 'Actualizando…';
      else if (ready) $text.textContent = 'Suelta para refrescar';
      else $text.textContent = 'Desliza para refrescar';
    }
  };

  const onTouchStart = (e) => {
    if (loading) return;
    // CB-T2 (bit #3257): un overlay position:fixed anidado dentro de `container`
    // (ej. el panel lateral de Argemiro Reconteo) tiene SU PROPIO scroll interno,
    // pero como es descendiente en el DOM, sus eventos touch burbujean hasta aca.
    // container.closest('[data-scroll]') revisa el scroll de la PAGINA, no el del
    // overlay -- con la pagina en top=0 (comun: el overlay fixed no la mueve) el
    // gesto se interpretaba como pull-to-refresh y el 'onRefresh' (re-render
    // completo del tab) cerraba el panel y reiniciaba el listado a mitad de
    // scroll. Marcar overlays con data-no-ptr para excluirlos.
    if (e.target.closest('[data-no-ptr]')) return;
    const scrollable = container.closest('[data-scroll]') || window;
    const scrollTop = scrollable === window
      ? (window.scrollY || document.documentElement.scrollTop || 0)
      : scrollable.scrollTop;
    if (scrollTop > 0) return;
    tracking = true;
    startY = e.touches[0].clientY;
    currentDelta = 0;
    $header.setAttribute('data-active', 'true');
  };
  const onTouchMove = (e) => {
    if (!tracking || loading) return;
    const y = e.touches[0].clientY;
    const delta = y - startY;
    if (delta <= 0) { setHeight(0); return; }
    // Use a dampening factor so it feels rubber-bandy
    currentDelta = Math.min(delta * 0.55, THRESHOLD * 1.5);
    setHeight(currentDelta);
    setState(currentDelta >= THRESHOLD, false);
    if (delta > 5) {
      // Prevent over-scroll bounce while pulling
      if (e.cancelable) e.preventDefault();
    }
  };
  const onTouchEnd = async () => {
    if (!tracking) return;
    tracking = false;
    $header.removeAttribute('data-active');
    const now = Date.now();
    if (currentDelta >= THRESHOLD && (now - lastFire) >= THROTTLE_MS) {
      lastFire = now;
      loading = true;
      setState(true, true);
      setHeight(44);
      try {
        await onRefresh();
      } catch { /* silent */ }
      loading = false;
    }
    setState(false, false);
    setHeight(0);
    currentDelta = 0;
  };

  container.addEventListener('touchstart', onTouchStart, { passive: true });
  container.addEventListener('touchmove', onTouchMove, { passive: false });
  container.addEventListener('touchend', onTouchEnd, { passive: true });
  container.addEventListener('touchcancel', onTouchEnd, { passive: true });

  const cleanup = () => {
    container.removeEventListener('touchstart', onTouchStart);
    container.removeEventListener('touchmove', onTouchMove);
    container.removeEventListener('touchend', onTouchEnd);
    container.removeEventListener('touchcancel', onTouchEnd);
    container.__ptrBound = false;
    container.__ptrCleanup = null;
  };

  container.__ptrBound = true;
  container.__ptrCleanup = cleanup;
  return cleanup;
}
